import { DriveItemListAction, DriveItemListActionKeys } from './actions';
import { ListDisplayType } from './constants';
import { Api } from '../../typings/api';
import Utils from '../../utils';
import LocalStorageManager from '../../utils/localStorage/manager';
import UserAgentUtils from '../../utils/userAgent';

type ContextualMenuState = ContextualMenuStateClosed | ContextualMenuStateOpened;

interface ContextualMenuStateClosed {
	isOpened: false;
	isLocked: boolean;
}

interface ContextualMenuStateOpened {
	isOpened: true;
	isLocked: boolean;
	driveItemId: number;
}

interface FolderState {
	id: number;
	label: string;
}

interface CustomTemplates {
	ids: number[];
	isLoaded: boolean;
}

interface FolderTreeUpdate {
	lastDate?: number; // Timestamp
	isUpdating: boolean;
}

interface DriveListState {
	driveItemsFetchStatus: { [k: number]: DriveItemFetchState};
	driveItems: DriveItemInfoMap;
	resolutions: { [k: string]: Api.ResolutionGroup };
	displayType: ListDisplayType;
	foldersDisplayType: { [k: string]: ListDisplayType };
	fetchAllFetchStatus: FetchStatus;
	folderTreeUpdate: FolderTreeUpdate;
	contextualMenu: ContextualMenuState;
	breadcrumbs: FolderState[]; // used for navigation in ResourceSelector
	isEditMode: boolean;
	isLoading: boolean; // used for blocking screen when some editing operation happens (e.g., moving or removing item)
	customTemplates: CustomTemplates;
}

type DriveItemInfoMap = { [k: number]: DriveItemInfo };

interface Dependency {
	id: number;
	version: number;
}

interface DriveItemInfo extends Api.DriveFolderInfo {
	isInGallery?: boolean;
	dependencies?: Dependency[];
	isTemplate?: boolean;
}

enum FetchStatus {
	NOT_FETCHED = 'NOT_FETCHED',
	FETCHING = 'FETCHING',
	FETCHED = 'FETCHED',
	ERROR = 'ERROR'
}

const initialState: DriveListState = {
	driveItemsFetchStatus: {},
	driveItems: {},
	resolutions: {},
	folderTreeUpdate: {
		lastDate: undefined,
		isUpdating: true,
	},
	fetchAllFetchStatus: FetchStatus.NOT_FETCHED,
	displayType: ListDisplayType.Default,
	foldersDisplayType: {},
	contextualMenu: {
		isOpened: false,
		isLocked: false,
	},
	breadcrumbs: [],
	isEditMode: false,
	isLoading: false,
	customTemplates: {
		ids: [],
		isLoaded: false,
	},
};

interface DriveItemFetchState {
	metaFetchStatus: FetchStatus;
	contentBlocksStatus: { [k: number]: FetchStatus };
}

const driveItemFolderInitialState: DriveItemFetchState = {
	metaFetchStatus: FetchStatus.NOT_FETCHED,
	contentBlocksStatus: {}
};

function driveList(state: DriveListState = initialState, action: DriveItemListAction): DriveListState {
	if (action.type === DriveItemListActionKeys.UPDATE_LIST_DISPLAY_TYPE) {
		return { ...state, displayType: action.displayType };
	}

	let newState = Utils.deepCopy(state);
	let driveListItem;
	let driveItemId;

	if (action.type === DriveItemListActionKeys.REQUEST_DRIVE_META
	|| action.type === DriveItemListActionKeys.RECEIVE_DRIVE_META
	|| action.type === DriveItemListActionKeys.FETCH_ERROR_DRIVE_META) {
		driveItemId = action.driveItemId;
	}

	if (action.type === DriveItemListActionKeys.REQUEST_CONTENT_BLOCK
	|| action.type === DriveItemListActionKeys.RECEIVE_CONTENT_BLOCK
	|| action.type === DriveItemListActionKeys.FETCH_ERROR_CONTENT_BLOCK) {
		driveItemId = action.driveFolderId;
	}

	if (driveItemId) {
		driveListItem = getDriveItemStatusFromMap(newState.driveItemsFetchStatus, driveItemId);
	}

	switch (action.type) {
		case DriveItemListActionKeys.REQUEST_DRIVE_META:
			driveListItem.metaFetchStatus = FetchStatus.FETCHING;
			break;
		case DriveItemListActionKeys.RECEIVE_DRIVE_META:
			driveListItem.metaFetchStatus = FetchStatus.FETCHED;
			break;
		case DriveItemListActionKeys.FETCH_ERROR_DRIVE_META:
			driveListItem.metaFetchStatus = FetchStatus.ERROR;
			break;
		case DriveItemListActionKeys.REQUEST_CONTENT_BLOCK:
			driveListItem.contentBlocksStatus[action.block] = FetchStatus.FETCHING;
			break;
		case DriveItemListActionKeys.RECEIVE_CONTENT_BLOCK:
			driveListItem.contentBlocksStatus[action.block] = FetchStatus.FETCHED;
			break;
		case DriveItemListActionKeys.FETCH_ERROR_CONTENT_BLOCK:
			driveListItem.contentBlocksStatus[action.block] = FetchStatus.ERROR;
			break;
		case DriveItemListActionKeys.RECEIVE_DRIVE_ITEMS:
			let driveItemsInfos: DriveItemInfoMap = {};
			for (const driveItemId in action.driveItems) {
				if (action.driveItems.hasOwnProperty(driveItemId)) {
					const driveItemInfo = action.driveItems[driveItemId];
					driveItemsInfos[driveItemId] = {
						...newState.driveItems[driveItemId],
						...driveItemInfo,
					};
					// if isInGallery does not exist, use the default one sent
					// (for example it exists in the save in localstorage)
					if (driveItemsInfos[driveItemId].isInGallery === undefined) {
						driveItemsInfos[driveItemId].isInGallery = action.isInGallery;
					}
				}
			}
			newState.driveItems = mergeDriveItems(newState.driveItems, driveItemsInfos);
			break;
		case DriveItemListActionKeys.REMOVE_DRIVE_ITEMS:
			action.driveItemIds.forEach(driveItemId => delete newState.driveItems[driveItemId]);
			break;
		case DriveItemListActionKeys.RECEIVE_RESOLUTIONS:
			newState.resolutions = mergeResolutions(newState.resolutions, action.resolutions);
			break;
		case DriveItemListActionKeys.RESET_FETCH_ALL_STATUS:
			newState.fetchAllFetchStatus = FetchStatus.NOT_FETCHED;
			break;
		case DriveItemListActionKeys.REQUEST_ALL:
			newState.fetchAllFetchStatus = FetchStatus.FETCHING;
			break;
		case DriveItemListActionKeys.RECEIVE_ALL:
			newState.fetchAllFetchStatus = FetchStatus.FETCHED;
			break;
		case DriveItemListActionKeys.OPEN_CONTEXTUAL_MENU:
			newState.contextualMenu = {
				isOpened: true,
				isLocked: false,
				driveItemId: action.driveItemId,
			};
			break;
		case DriveItemListActionKeys.CLOSE_CONTEXTUAL_MENU:
			newState.contextualMenu = {
				isOpened: false,
				isLocked: false,
			};
			break;
		case DriveItemListActionKeys.LOCK_CONTEXTUAL_MENU:
			newState.contextualMenu = {
				...state.contextualMenu,
				isLocked: true,
			};
			break;
		case DriveItemListActionKeys.UNLOCK_CONTEXTUAL_MENU:
			newState.contextualMenu = {
				...state.contextualMenu,
				isLocked: false,
			};
			break;
		case DriveItemListActionKeys.SET_BREADCRUMBS:
			const itemIndex = newState.breadcrumbs.findIndex(({ id }) => id === action.item.id);
			newState.breadcrumbs = itemIndex === -1
				? [...newState.breadcrumbs, action.item]
				: newState.breadcrumbs.slice(0, itemIndex + 1);
			break;
		case DriveItemListActionKeys.RESET_BREADCRUMBS:
			newState.breadcrumbs = [];
			break;
		case DriveItemListActionKeys.UPDATE_DRIVE_ITEM:
			newState.driveItems[action.item.id] = {
				...newState.driveItems[action.item.id],
				...action.item,
			};
			break;
		case DriveItemListActionKeys.TOGGLE_EDIT_MODE:
			newState.isEditMode = !state.isEditMode;
			break;
		case DriveItemListActionKeys.MOVE_DRIVE_ITEM: {
			const { folderId, filteredResources, from, to, isAppRootFolder, privateDirId } = action;
			const resourcesWithoutMovedItem: Api.DriveItemResource[] = [];
			const isMoveToTop = to - from < 0;

			filteredResources!.forEach((resource, index) => {
				const isMovedItem = index === from;
				if (!isMovedItem) {
					const shouldUpdatePosition = isMoveToTop
						? index >= to && index <= from
						: index >= from && index <= to - 1;
					const positionShift = isMoveToTop ? -1 : 1;
					const updatedResource = shouldUpdatePosition
						? {
							...resource,
							position: resource.position as number + positionShift,
						}
						: resource;
					resourcesWithoutMovedItem.push(updatedResource);
				}
			});

			const movedItem = filteredResources![from];
			const newMovedItemIndex = isMoveToTop ? to : to - 1;
			const newMovedItemPosition = filteredResources![newMovedItemIndex].position;

			resourcesWithoutMovedItem.splice(newMovedItemIndex, 0, {
				...movedItem,
				position: newMovedItemPosition,
			});

			if (isAppRootFolder) {
				// privateDir must be kept in store
				const { resources: allResources } = newState.driveItems[folderId];
				const privateDir = allResources!.find(({ id }) => id === privateDirId);

				if (privateDir) {
					resourcesWithoutMovedItem.push(privateDir);
				}
			}

			newState.driveItems[folderId].resources = resourcesWithoutMovedItem;

			break;
		}
		case DriveItemListActionKeys.MOVE_DRIVE_ITEM_IN_FOLDER: {
			const { resources } = newState.driveItems[action.folderId];
			newState.driveItems[action.folderId].resources = resources!.filter(({ id }) => (
				id !== action.id
			));
			break;
		}
		case DriveItemListActionKeys.RESET_DRIVE_ORDER:
			newState.driveItems[action.folderId].resources = action.resources;
			break;
		case DriveItemListActionKeys.SET_LOADING_STATUS:
			newState.isLoading = action.isLoading;
			break;
		case DriveItemListActionKeys.SET_FOLDER_DISPLAY_TYPE:
			newState.foldersDisplayType[action.folderId] = action.displayType;
			break;
		case DriveItemListActionKeys.SET_FOLDERS_DISPLAY_TYPE:
			newState.foldersDisplayType = action.data;
			break;
		case DriveItemListActionKeys.RESET_FOLDERS_DISPLAY_TYPE:
			newState.foldersDisplayType = {};
			break;
		case DriveItemListActionKeys.SET_CUSTOM_TEMPLATES_IDS:
			newState.customTemplates = {
				ids: action.ids,
				isLoaded: true,
			};
			break;
		case DriveItemListActionKeys.ADD_CUSTOM_TEMPLATE:
			newState.customTemplates = {
				...newState.customTemplates,
				ids: [
					...newState.customTemplates.ids,
					action.id,
				],
			};
			break;
		case DriveItemListActionKeys.REMOVE_CUSTOM_TEMPLATE:
			newState.customTemplates = {
				...newState.customTemplates,
				ids: newState.customTemplates.ids.filter(id => id !== action.id),
			};
			break;
		case DriveItemListActionKeys.SET_LAST_FOLDER_TREE_UPDATE:
			newState.folderTreeUpdate.lastDate = action.lastFolderTreeUpdate;
			newState.folderTreeUpdate.isUpdating = false;
			break;
		case DriveItemListActionKeys.START_FOLDER_TREE_UPDATE:
			newState.folderTreeUpdate.isUpdating = true;
			break;
		case DriveItemListActionKeys.END_FOLDER_TREE_UPDATE:
			newState.folderTreeUpdate.isUpdating = false;
			break;
		default:
			return state;
	}

	if (driveItemId) {
		newState.driveItemsFetchStatus[driveItemId] = driveListItem;
	}

	if (Utils.stringIsInStringEnum(action.type, DriveItemListActionKeys)) {
		if (UserAgentUtils.isOnline() && UserAgentUtils.isMobileApp) {
			LocalStorageManager.setStoreDriveList(newState);
		}
	}

	return newState;
}

// Function to merge maps of drive items that ensure no information is lost
function mergeDriveItems(map1: DriveItemInfoMap, map2: DriveItemInfoMap): DriveItemInfoMap {
	let resultMap: DriveItemInfoMap = Utils.deepCopy(map1);

	Object.keys(map2).forEach(key => {
		const id = parseInt(key, 10);
		if (resultMap[id] && resultMap[id].resources && !map2[id].resources) {
			const resources = Utils.deepCopy(resultMap[id].resources);
			resultMap[id] = Utils.deepCopy(map2[id]);
			resultMap[id].resources = resources;
			return;
		}

		resultMap[id] = Utils.deepCopy(map2[id]);
	});
	return resultMap;
}

// Function to merge maps of resolutions that ensure no resolution is overriden
function mergeResolutions(map1: { [k: string]: Api.ResolutionGroup }, map2: { [k: string]: Api.ResolutionGroup }): { [k: string]: Api.ResolutionGroup } {
	let resultMap: { [k: string]: Api.ResolutionGroup } = Utils.deepCopy(map1);

	Object.keys(map2).forEach(key => {
		if (resultMap[key]) {
			const itemFromMap2 = map2[key];
			const itemFromResultMap = resultMap[key];

			if (itemFromMap2.sizes && itemFromResultMap.sizes
				&& itemFromMap2.sizes.length > itemFromResultMap.sizes.length) {
				resultMap[key] = Utils.deepCopy(map2[key]);
			}
		} else {
			resultMap[key] = Utils.deepCopy(map2[key]);
		}
	});
	return resultMap;
}

function getDriveItemStatusFromMap(driveItemsFetchStatus: { [k: number]: DriveItemFetchState }, folderId: number): DriveItemFetchState {
	return driveItemsFetchStatus[folderId] ? driveItemsFetchStatus[folderId] : Utils.deepCopy(driveItemFolderInitialState);
}

export {
	DriveListState,
	DriveItemInfoMap,
	DriveItemInfo,
	FetchStatus,
	DriveItemFetchState,
	driveList,
	getDriveItemStatusFromMap,
	FolderState,
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/DriveItemList/reducer.ts